<!-- Responsibility: Implement ability args dialog behavior for the ui/components/dialog surface.
Scope: Owns ui/components/dialog concerns only; cross-surface orchestration is excluded. -->
<ability-args-dialog class="ability-args-dialog">
  <raw-html if={ ability()?.desc } content={ ability().desc } mode="prose" class="fm-desc"></raw-html>

  <div class="form-fields" ref="formHost"></div>
  <p if={ state.errorMessage } class="ability-args-error">{ state.errorMessage }</p>

  <div class="ability-args-actions">
    <button type="button" class="action-btn primary" onclick={ confirm }>Cast</button>
  </div>

  <script>
    import { AutoForm, collectValues, populateFields } from 'field-assist'
    import { sysNotify } from '../../../app-states.js'
    import * as gameStore from '../../../game-store.js'
    import { playEffect } from '../../../engine/audio.js'
    import { buildAbilityToolRef, resolveAbilityTargets } from '../../helpers/ability-actions.ts'
    import {
      buildAbilityArgFormMeta,
      buildAbilityArgInitialValues,
      buildAbilityArgSubmission,
    } from '../../helpers/ability-form.ts'
    import RawHtml from '../raw-html.riot'

    export default {
      components: { RawHtml },
      state: {
        openToken: 0,
        errorMessage: '',
      },

      onMounted() {
        this.resetForOpen(this.props)
      },

      onBeforeUpdate(newProps) {
        const nextToken = Number(newProps?.openToken ?? 0)
        if (nextToken !== Number(this.state.openToken ?? 0)) {
          this.resetForOpen(newProps)
        }
      },

      data() {
        return this.props.content ?? {}
      },

      ability() {
        return this.data()?.ability ?? null
      },

      targets() {
        return Array.isArray(this.data()?.targets) ? this.data().targets : []
      },

      actionContext() {
        const ctx = this.data()?.actionContext
        return ctx && typeof ctx === 'object' ? ctx : {}
      },

      renderForm(ability, targets) {
        const host = this.$('[ref="formHost"]')
        if (!(host instanceof HTMLElement)) return
        host.replaceChildren()
        if (!ability) return

        const initialValues = buildAbilityArgInitialValues(ability, targets)
        const autoForm = new AutoForm(buildAbilityArgFormMeta(ability, targets))
        autoForm.render(initialValues, host)
        populateFields(host, initialValues, 'name')

        requestAnimationFrame(() => {
          host.querySelector('[name]')?.focus?.()
        })
      },

      focusFirstInvalidField() {
        const host = this.$('[ref="formHost"]')
        host?.querySelector?.(':invalid')?.focus?.()
      },

      resetForOpen(props) {
        const nextToken = Number(props?.openToken ?? 0)
        const ability = props?.content?.ability ?? null
        const targets = props?.content?.targets ?? []
        this.update({
          openToken: nextToken,
          errorMessage: '',
        })
        requestAnimationFrame(() => {
          this.renderForm(ability, targets)
        })
      },

      async confirm() {
        playEffect('ui_click')
        const ability = this.ability()
        if (!ability) return
        const host = this.$('[ref="formHost"]')
        if (!(host instanceof HTMLElement)) return

        const values = collectValues(host, undefined, 'name')
        if (values?._errors && Object.keys(values._errors).length) {
          const errorMessage = 'Fill the highlighted ability fields before casting.'
          this.update({ errorMessage })
          sysNotify(errorMessage)
          this.focusFirstInvalidField()
          return
        }

        const { argsText, targetLabels } = buildAbilityArgSubmission(ability, values)
        const fullInput = argsText
          ? `Use ability: ${ability.name} | ${argsText}`
          : `Use ability: ${ability.name}`
        const actionContext = this.actionContext()
        const contextTargets = Array.isArray(actionContext?.explicitTargets) ? actionContext.explicitTargets : []
        const explicitTargets = resolveAbilityTargets(gameStore.state, targetLabels)
        const tags = [
          ...new Set([
            ...((Array.isArray(actionContext?.tags) ? actionContext.tags : []).filter(Boolean)),
            'ability_args_dialog',
            'ability_execute',
          ]),
        ]
        await this.props.requestModalClose?.('submit')
        gameStore.handleAction(fullInput, undefined, {
          source: actionContext?.source ?? 'player',
          mode: actionContext?.mode ?? 'do',
          category: actionContext?.category ?? 'ability',
          tool: actionContext?.tool ?? buildAbilityToolRef(ability),
          explicitTargets: explicitTargets.length ? explicitTargets : contextTargets,
          tags,
        })
      },
    }
  </script>

  <style>
    ability-args-dialog {
      display: block;
      padding-top: var(--space-xs);
    }

    .form-fields {
      display: grid;
      gap: var(--space-sm);
    }

    .ability-args-error {
      margin-top: var(--space-sm);
      color: var(--danger-ink, #ffb6b6);
      font: var(--font-ui-small);
    }

    .ability-args-actions {
      display: flex;
      justify-content: flex-end;
      margin-top: var(--space-md);
    }
  </style>
</ability-args-dialog>
